import { useState } from "react";

import { useApiUrl, useDataProvider } from "@refinedev/core";
import { z } from "zod";

import { RemoteMultiSelect } from "@/components/fields/remote-multi-select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEventUpdates } from "@/pages/events/features/notifications/event-updates-context";
import { genreNestedSchema } from "@/pages/genres/schemas/genre.nested.form";

const Lookup = z.object({
  id: z.string(),
  name: z.string(),
});

export function OfferAddedModal() {
  const { isOfferAddedModalOpen, closeOfferAddedModal, sendEventUpdates } =
    useEventUpdates();
  const [shortArtistState, setShortArtistState] = useState<
    z.infer<typeof Lookup>[]
  >([]);
  const [shortGenresState, setShortGenresState] = useState<
    z.infer<typeof genreNestedSchema>[]
  >([]);
  const [shortExcludedGenresState, setShortExcludedGenresState] = useState<
    z.infer<typeof genreNestedSchema>[]
  >([]);
  const [shortEventsState, setShortEventsState] = useState<
    z.infer<typeof Lookup>[]
  >([]);

  const apiUrl = useApiUrl();
  const { custom } = useDataProvider()();

  const handleSendOfferAddedNotification = () => {
    sendEventUpdates("offer_added_v2", {
      artistIds: shortArtistState.map((a) => a.id),
      similarEventIds: shortEventsState.map((e) => e.id),
      genreIds: shortGenresState.map((g) => g.id),
      excludeGenreIds: shortExcludedGenresState.map((g) => g.id),
    });
    handleClose();
  };

  const handleClose = () => {
    closeOfferAddedModal();
    setShortArtistState([]);
    setShortEventsState([]);
    setShortGenresState([]);
    setShortExcludedGenresState([]);
  };

  return (
    <Dialog
      open={isOfferAddedModalOpen}
      onOpenChange={(open) => !open && handleClose()}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Select Audience for Added Offer</DialogTitle>
          <DialogDescription>
            Choose related artists, similar events and genres to create a more
            broad audience and increase conversion.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="text-sm">Similar Artists</div>
          <RemoteMultiSelect<z.infer<typeof Lookup>>
            value={shortArtistState}
            triggerType="change"
            showSelected
            onValueChanged={(value) => {
              console.debug("VALUE CHANGED", value);
              setShortArtistState(value?.map((v) => Lookup.parse(v)) ?? []);
            }}
            accessorFn={(option) => ({
              id: option?.id || option?.name,
              name: option ? `${option?.name}` : "",
              // iconUrl: option?.media?.photos[0]?.url,
            })}
            onSearch={async (query, onStatusChanged) => {
              if (!query) {
                return [];
              }
              onStatusChanged("loading");
              const response = await custom?.({
                url: `${apiUrl}/artists/explore`,
                method: "get",
                query: {
                  q: query ?? "*",
                  limit: 20,
                  page: 0,
                  filters: "",
                  sortBy: "",
                },
              });

              onStatusChanged("success");

              if (!response?.data) {
                return [];
              }

              console.debug("response", response?.data?.hits);

              return response?.data?.hits?.map((item: any) =>
                Lookup.parse(item?.document),
              );
            }}
          />
          <div className="text-sm">Similar Genres</div>
          <RemoteMultiSelect<z.infer<typeof Lookup>>
            showSelected
            value={shortGenresState}
            triggerType="change"
            onValueChanged={(value) => {
              setShortGenresState(value?.map((v) => Lookup.parse(v)) ?? []);
            }}
            accessorFn={(option) => ({
              id: option?.id || option?.name,
              name: option ? `${option?.name}` : "",
              // iconUrl: option?.media?.photos[0]?.url,
            })}
            onSearch={async (query, onStatusChanged) => {
              if (!query) {
                return [];
              }
              onStatusChanged("loading");
              const response = await custom?.({
                url: `${apiUrl}/genres/explore`,
                method: "get",
                query: {
                  q: query ?? "*",
                  limit: 20,
                  page: 0,
                  filters: "",
                  sortBy: "",
                },
              });

              onStatusChanged("success");

              if (!response?.data) {
                return [];
              }

              console.debug("response", response?.data?.hits);

              return response?.data?.hits?.map((item: any) =>
                Lookup.parse(item?.document),
              );
            }}
          />
          <div className="text-sm">Similar Events</div>
          <RemoteMultiSelect<z.infer<typeof Lookup>>
            showSelected
            value={shortEventsState}
            triggerType="change"
            onValueChanged={(value) => {
              setShortEventsState(value?.map((v) => Lookup.parse(v)) ?? []);
            }}
            accessorFn={(option) => ({
              id: option?.id || option?.name,
              name: option ? `${option?.name}` : "",
              // iconUrl: option?.media?.photos[0]?.url,
            })}
            onSearch={async (query, onStatusChanged) => {
              if (!query) {
                return [];
              }
              onStatusChanged("loading");
              const response = await custom?.({
                url: `${apiUrl}/events/explore`,
                method: "get",
                query: {
                  q: query ?? "*",
                  limit: 20,
                  page: 0,
                  filters: "",
                  sortBy: "",
                },
              });

              onStatusChanged("success");

              if (!response?.data) {
                return [];
              }

              console.debug("response", response?.data?.hits);

              return response?.data?.hits?.map((item: any) =>
                Lookup.parse(item?.document),
              );
            }}
          />
          <div className="text-sm">Excluded Genres</div>
          <RemoteMultiSelect<z.infer<typeof Lookup>>
            showSelected
            value={shortExcludedGenresState}
            triggerType="change"
            onValueChanged={(value) => {
              setShortExcludedGenresState(
                value?.map((v) => Lookup.parse(v)) ?? [],
              );
            }}
            accessorFn={(option) => ({
              id: option?.id || option?.name,
              name: option ? `${option?.name}` : "",
              // iconUrl: option?.media?.photos[0]?.url,
            })}
            onSearch={async (query, onStatusChanged) => {
              if (!query) {
                return [];
              }
              onStatusChanged("loading");
              const response = await custom?.({
                url: `${apiUrl}/genres/explore`,
                method: "get",
                query: {
                  q: query ?? "*",
                  limit: 20,
                  page: 0,
                  filters: "",
                  sortBy: "",
                },
              });

              onStatusChanged("success");

              if (!response?.data) {
                return [];
              }

              return response?.data?.hits?.map((item: any) =>
                Lookup.parse(item?.document),
              );
            }}
          />
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSendOfferAddedNotification}
            disabled={
              shortArtistState.length === 0 &&
              shortEventsState.length === 0 &&
              shortGenresState.length === 0 &&
              shortExcludedGenresState.length === 0
            }
          >
            Send Announcement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
